<template>
  <base-card>
    <div v-if="loading" class="p-10 text-center text-indigo-600">
      <span class="block flex-none spinner-border" role="status" aria-hidden="true"></span>
    </div>

    <div v-else-if="dropzoneUploading || showDropzone">
        <div v-if="dropzoneUploading" class="min-h-[200px] space-y-4 text-center text-indigo-600">
          <p>Please wait while the files are being uploaded...</p>
          <p>{{ dropzoneUploadIndex }} of {{ dropzoneFiles.length}}</p>
          <span class="block flex-none spinner-border" role="status" aria-hidden="true"></span>
        </div>
        <dropzone v-else v-model="dropzoneFiles" v-on:fileadded="dropzoneUpdated" previewless :compact="true" ref="dropzone" />
        <div  class="text-center my-4">
          <base-button small @click="showDropzone = false">Cancel File Drop</base-button>
        </div>
      </div>
    <div v-else class="space-y-4 flex flex-col max-h-screen h-full">
      <div class="border-b border-gray-200 border-solid py-4 flex justify-between flex-none">
        <div class="flex items-center gap-4">
          <base-tooltip class="flex items-center">
            <template slot="content">Go Back</template>
            <button type="button" @click="goBack" :disabled="!path || path == '/'" class="disabled:opacity-60 disabled:cursor-not-allowed"><ArrowCircleLeftIcon class="w-8 h-8 text-gray-600" /></button>
          </base-tooltip>
          <p class="text-xl font-bold">/{{ path }}</p>
        </div>
        <div class="flex items-center gap-4">
          <base-tooltip class="flex items-center">
            <template slot="content">Upload a new file in the current directory</template>
            <label :for="`mediamanager-${uuid}-upload`" class="inline-block relative">
              <UploadIcon class="w-8 h-8 text-gray-600 relative z-10 cursor-pointer" />
              <input type="file" :name="`mediamanager-${uuid}-upload`" :id="`mediamanager-${uuid}-upload`" @change="addFile"
                class="block opacity-0 absolute top-0 left-0 w-full h-full z-0 cursor-pointer" />
            </label>
          </base-tooltip>
          <base-tooltip class="flex items-center">
            <template slot="content">Add a new folder in the current directory</template>
            <button type="button" @click="addFolder"><FolderAddIcon class="w-8 h-8 text-gray-600" /></button>
          </base-tooltip>
        </div>

      </div>

      <div class="flex items-stretch divide-x divide-gray-200 gap-4 max-h-full flex-1 h-96">
        <div class="w-2/3 divide-y divide-gray-200 overflow-auto max-h-full">
          <div v-for="(directory, idx) in directories"  class="p-2 w-full relative">
            <button type="button" @click="changeDirectory(directory)" class="flex w-full items-center gap-2">
              <div class="relative inline-block w-8 h-8">
                <svg class="absolute inset-0 m-auto w-full h-full opacity-80">
                  <use :href="`#filetype-folder`" />
                </svg>
              </div>
              <span>{{ directory.split('/').pop() }}</span>
            </button>
            <div class="absolute z-10 right-2 top-1/2 -translate-y-1/2 w-8 h-8">
              <actions-popover :ref="`mediaMangerFolderActions-${idx}`">
                <actions-button @click.prevent="renameFolder(directory)">
                  <pencil-alt-icon class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                  Rename
                </actions-button>
                <actions-button @click.prevent="deleteFolder(directory)">
                  <trash-icon class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                  Delete
                </actions-button>
              </actions-popover>
            </div>
          </div>
          <p v-if="!files.length" class="text-gray-600 p-4 pt-0">No files in this directory. Add files and folders using the icons at the top right.</p>
          <button type="button" v-for="file in files" @click="selectFile(file)" @dblclick="useFile(file)" class="p-2 flex w-full relative items-center gap-2 rounded overflow-hidden focus:bg-blue-600 focus:text-white">
            <div class="relative inline-block w-8 h-8">
              <svg class="absolute inset-0 m-auto w-full h-full opacity-80">
                <use v-if="fileIconExists(file)" :href="`#filetype-${fileExtension(file)}`" />
                <use v-else :href="`#filetype-blank`" />
              </svg>
            </div>
            <!-- <DocumentIcon class="w-8 h-8 text-gray-600" /> -->
            <span>{{ file.split('/').pop() }}</span>
          </button>
        </div>
        <div class="w-1/3 p-8 flex-none max-h-full">
          <div v-if="selectedFile" class="text-center flex flex-col h-full overflow-hidden">
            <!-- If is image type, show preview -->
            <div class="flex-auto h-1/2 overflow-auto">
              <img v-if="isImage(selectedFile.url)" :src="selectedFile.url" class="inline-block w-96 max-w-full h-auto"  :title="selectedFile.name" :alt="selectedFile.name" />
              <div v-else-if="fileExtension(selectedFile.url)"  class="relative inline-block w-1/2 pt-[50%]">
                <svg class="absolute inset-0 m-auto w-full h-full opacity-60" :title="selectedFile.name">
                  <use v-if="fileIconExists(selectedFile.url)" :href="`#filetype-${fileExtension(selectedFile.url)}`" />
                  <use v-else :href="`#filetype-blank`" />
                </svg>
              </div>
              <DocumentIcon v-else class="inline-block w-1/2 h-auto opacity-60" :title="selectedFile.name" />
            </div>
            <div class="flex-none">
              <p class="mt-5 truncate" :title="selectedFile.name">{{ selectedFile.name }}</p>
              <p>{{ fileType(selectedFile.url) }}</p>
              <p>{{ humanFileSize(selectedFile.size, true) }}</p>
              <p class="mt-5 flex justify-center items-center gap-3">
                <base-tooltip v-if="modal">
                  <template slot="content">Loads the file into the WYSIWYG and <br>closes the Media Manager modal.</template>
                  <base-button small @click="useFile(selectedFile.path)">Use File</base-button>
                </base-tooltip>
                <base-tooltip>
                  <template slot="content">Right click and click "Save Link As" to download. <br>Clicking the link will open the file in a new tab.</template>
                  <base-button tag="a" small :href="selectedFile.url" target="_blank">Open File</base-button>
                </base-tooltip>
              </p>
              <p class="mt-5 flex justify-center items-center gap-5 text-gray-900">
                <base-tooltip>
                  <template slot="content">Rename File</template>
                  <base-button icon @click="renameFile(selectedFile.path)"><PencilAltIcon class="w-5 h-5 text-yellow-500" /></base-button>
                </base-tooltip>
                <base-tooltip>
                  <template slot="content">Delete File</template>
                  <base-button icon @click="deleteFile(selectedFile.path)"><TrashIcon class="w-5 h-5 text-red-500" /></base-button>
                </base-tooltip>
              </p>
            </div>
          </div>
          <span v-else>Select a file to view details</span>
        </div>
      </div>
    </div>
  </base-card>
</template>
<script>
  import Actions from '../Actions'
  import { ArrowCircleLeftIcon, DocumentIcon, DocumentTextIcon, FolderAddIcon, FolderIcon, UploadIcon } from '@vue-hero-icons/outline';
  import { PencilAltIcon, TrashIcon } from '@vue-hero-icons/solid'

  import BaseTooltip from '../Base/BaseTooltip.vue';
  import mime from 'mime'
  import Dropzone from '../Form/Dropzone.vue';

  export default {
    name: 'MediaManager',
    components: {
      ...Actions,
      ArrowCircleLeftIcon,
      DocumentIcon,
      DocumentTextIcon,
      FolderIcon,
      FolderAddIcon,
      UploadIcon,
      PencilAltIcon,
      TrashIcon,
      BaseTooltip,
      Dropzone
    },
    props: {
      modal: {
        type: Boolean,
        default: false
      },
    },
    data() {
      return {
        disk: '',
        path: '',
        directories: [],
        files: [],
        selectedFile: null,
        loading: true,
        dropzoneFiles: [],
        dropzoneTimeout: null,
        dropzoneUploading: false,
        dropzoneUploadIndex: 0,
        showDropzone: false,
      }
    },
    methods: {
      changeDirectory(newPath) {
        this.selectedFile = null;
        this.loading = true;
        this.$CMS_GET('media.index', {disk: this.disk, path: newPath}, true).then((data) => {
          this.directories = data.directories;
          this.files = data.files;
          this.path = newPath;
          if (!this.modal) {
            window.history.replaceState({path: newPath}, "", route('cms.media.index', {path: newPath, disk: this.disk}))
          }
          setTimeout(() => {
            this.loading = false
          }, 200);
        });
      },
      goBack() {
        const pathArray = this.path.split('/');
        pathArray.pop();
        this.changeDirectory(pathArray.join('/'));
      },
      selectFile(file) {
        if (this.selectedFile?.path !== file) {
          this.$CMS_GET('media.show', {media: 'select', path: file, disk: this.disk}, true).then(data => {
            this.selectedFile = data
          });
        } else {
          console.log(file.concat(' already selected.'));
        }
      },
      isImage(file) {
        const extensions = ['jpg', 'jpeg', 'png', 'gif', 'svg'];
        const arr = file.split('.');
        return file && extensions.includes(arr[arr.length - 1].toLowerCase());
      },
      fileExtension(file) {
        const arr = file.split('.');
        const type = arr[arr.length - 1].toLowerCase();
        return type;
      },
      fileType(file) {
        const arr = file.split('.');
        const type = arr[arr.length - 1].toLowerCase();
        return mime.getType(type);
      },
      fileIconExists(file) {
        const ext = this.fileExtension(file);
        return window.document.getElementById(`filetype-${ext}`) !== null;
      },
      addFile(ev) {
        if (ev.target && ev.target.files && ev.target.files[0]) {
          this.uploadFiles(ev.target.files[0], true);
        } else {
          console.log('Nothing to do.');
        }
      },
      uploadFiles(files, cdAfter) {
        const formData = new FormData();
        formData.append('path', this.path);
        formData.append('disk', this.disk);
        formData.append('subject', 'file');
        formData.append('files', files);
        this.$CMS_POST('media.store', {}, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(data => {
          if (data.status == 'success') {
            if (cdAfter) {
              this.dropzoneUploadIndex = 0;
              this.dropzoneUploading = false;
              this.showDropzone = false;
              this.changeDirectory(this.path);
            }
          } else {
            console.error('failed to upload files', data);
          }
        })
      },
      renameFile(file) {
        this.$swal.fire({
          title: 'Rename File',
          html: `<p>Renaming a folder can have unintended consequences such as broken links to this file if it has been referenced. If you are sure you wish to rename this file, enter the new file name below.</p><form id="swal-rename-file-form"><input type="text" id="swal-rename-file-name" class="swal2-input" placeholder="File name..." /></form>`,
          confirmButtonText: 'Submit',
          focusConfirm: false,
          preConfirm: () => {
            const fileName = this.$swal
              .getPopup()
              .querySelector('#swal-rename-file-name');
            if (!fileName.value) {
              this.$swal.showValidationMessage(`Please include a name`);
            }
            return { fileName: fileName.value };
          },
        })
        .then((result) => {
          if (result.isConfirmed) {
            // result.value.fileName
            this.$CMS_UPDATE('media', {media: 'file'}, {
              disk: this.disk,
              path: this.path,
              from: file.split('/').pop(),
              to: result.value.fileName,
            }, true).then(() => {
              this.changeDirectory(this.path);
            });
          }
        });
      },
      deleteFile(file) {
        this.$swal.fire({
          title: 'Are you sure you want to delete this?',
          showDenyButton: false,
          showCancelButton: true,
        })
        .then((result) => {
          if (result.isConfirmed) {
            // Do delete
            this.$CMS_DELETE('media', {
              media: 'file',
              path: file,
              disk: this.disk
            }).then(() => {
              this.changeDirectory(this.path);
            });
          }
        });
      },
      addFolder() {
        this.$swal.fire({
          title: 'Create Folder',
          html: `<form id="swal-add-folder-form"><input type="text" id="swal-add-folder-name" class="swal2-input" placeholder="Folder name..." /></form>`,
          confirmButtonText: 'Create',
          focusConfirm: false,
          preConfirm: () => {
            const folderName = this.$swal
              .getPopup()
              .querySelector('#swal-add-folder-name');
            if (!folderName.value) {
              this.$swal.showValidationMessage(`Please include a name`);
            }
            return { folderName: folderName.value };
          },
        })
        .then((result) => {
          if (result.isConfirmed) {
            // result.value.folderName
            this.$CMS_POST('media.store', {}, {
              disk: this.disk,
              path: this.path,
              subject: 'folder',
              name: result.value.folderName,
            }).then(() => {
              this.changeDirectory(this.path);
            });
          }
        });
      },
      deleteFolder(folder) {
        this.$swal.fire({
          title: 'Are you sure you want to delete this?',
          showDenyButton: false,
          showCancelButton: true,
        })
        .then((result) => {
          if (result.isConfirmed) {
            // Do delete
            this.$CMS_DELETE('media', {
              media: 'folder',
              path: folder,
              disk: this.disk
            }).then(() => {
              this.changeDirectory(this.path);
            });
          }
        });
      },
      renameFolder(folder) {
        this.$swal.fire({
          title: 'Rename Folder',
          html: `<p>Renaming a folder can have unintended consequences such as broken links to files already referenced inside of this folder. If you are sure you wish to rename this folder, enter the new folder name below.</p><form id="swal-rename-folder-form"><input type="text" id="swal-rename-folder-name" class="swal2-input" placeholder="Folder name..." /></form>`,
          confirmButtonText: 'Submit',
          focusConfirm: false,
          preConfirm: () => {
            const folderName = this.$swal
              .getPopup()
              .querySelector('#swal-rename-folder-name');
            if (!folderName.value) {
              this.$swal.showValidationMessage(`Please include a name`);
            }
            return { folderName: folderName.value };
          },
        })
        .then((result) => {
          if (result.isConfirmed) {
            // result.value.folderName
            this.$CMS_UPDATE('media', {media: 'folder'}, {
              disk: this.disk,
              path: this.path,
              from: folder.split('/').pop(),
              to: result.value.folderName,
            }, true).then(() => {
              this.changeDirectory(this.path);
            });
          }
        });
      },
      useFile(file) {
        if (file == this.selectedFile.path && this.modal) {
          // Already selected so use selected file
          this.$emit('usefile', { detail: { url: this.selectedFile.url } });
        } else if (this.modal) {
          // Select the file then use selected file
          this.$CMS_GET('media.show', {media: 'select', path: file}).then(data => {
            this.selectedFile = data;
            this.$emit('usefile', { detail: { url: data.url } });
            console.log('Emitted usefile with ')
          });
        } else {
          //
        }
      },
      humanFileSize(bytes, si=false, dp=1) {
        const thresh = si ? 1000 : 1024;

        if (Math.abs(bytes) < thresh) {
          return bytes + ' B';
        }

        const units = si
          ? ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
          : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
        let u = -1;
        const r = 10**dp;

        do {
          bytes /= thresh;
          ++u;
        } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);


        return bytes.toFixed(dp) + ' ' + units[u];
      },
      dropzoneUpdated() {
        clearTimeout(this.dropzoneTimeout);
        this.dropzoneTimeout = setTimeout(() => {
          this.dropzoneUploadIndex = 0;
          this.dropzoneUploading = true;
          this.dropzoneFiles.forEach((item, index) => {
            ++this.dropzoneUploadIndex;
            this.uploadFiles(item.file, index == this.dropzoneFiles.length-1);
          });
        }, 500);
      },
    },
    mounted() {
      console.log('CMS Media Manager mounted!');
      if (!this.modal) {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        this.path = urlParams.get('path') || '';
        this.disk = urlParams.get('disk') || '';
      }
      this.changeDirectory(this.path);
      this.$el.addEventListener('dragover', () => {
        this.showDropzone = true;
      });
      this.$el.addEventListener('drop', () => {
        this.showDropzone = false;
      });
    }
  }
</script>
